/* The existing purchase block owns prices, quantity, bundles and cart requests. */
(() => {
  if (customElements.get('maison-sticky-cart')) return;
  customElements.define('maison-sticky-cart', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.section = this.closest('.shopify-section');
      this.confirmed = new Set();
      this.defaultTitle ||= this.section.querySelector('.maison-title')?.textContent.trim() || this.querySelector('[data-sticky-title]').textContent;
      this.image = this.querySelector('img');
      this.defaultImage = this.image?.getAttribute('src');
      this.action = this.querySelector('[data-sticky-action]');
      this.camera = this.querySelector('[data-sticky-camera]');
      this.mount = this.querySelector('[data-sticky-mount]');
      this.spacer = document.createElement('div');
      this.spacer.setAttribute('aria-hidden', 'true');
      document.body.append(this.spacer);
      this.resizeObserver = new ResizeObserver(() => {
        this.spacer.style.height = this.hidden ? '0px' : `${this.getBoundingClientRect().height}px`;
      });
      this.resizeObserver.observe(this);
      this.camera.addEventListener('click', () => this.scrollToSelection(), { signal });
      this.action.addEventListener('click', (event) => {
        if (!event.target.closest('button')) return;
        if (!this.ready()) return this.scrollToSelection();
        if (this.source?.busy || this.sourceButton?.disabled) return;
        // Clicking the real control retains its validation, gifts, bundles and drawer events.
        this.sourceButton?.click();
        this.sync();
      }, { signal });
      this.section.addEventListener('click', (event) => {
        const choice = event.target.closest('[data-mount-option], [data-opt-value], maison-product-buy [data-option]');
        if (!choice || !this.source?.contains(choice) || choice.disabled || choice.hasAttribute('data-unavailable')) return;
        this.groups().forEach((group, index) => {
          if (group.contains(choice)) this.confirmed.add(this.groupKey(index));
        });
        this.sync();
      }, { signal });
      this.section.addEventListener('input', () => this.scheduleSync(), { signal });
      document.documentElement.addEventListener('maison-mount:selected', (event) => {
        // The finder has already updated its owning block. Only confirm matching choices.
        const detail = event.detail || {};
        this.groups().forEach((group, index) => {
          const selected = group.querySelector('[data-mount-option][aria-selected="true"]');
          const sameId = selected?.dataset.variantId === String(detail.variantId);
          const sameMount = detail.variantTitle && this.source.mountKey?.(detail.variantTitle) === selected?.dataset.mountKey;
          if (selected && (sameId || sameMount)) this.confirmed.add(this.groupKey(index));
        });
        this.sync();
      }, { signal });
      // Product rerenders replace purchase controls; rebind without duplicating handlers.
      this.observer = new MutationObserver((records) => {
        if (records.some((record) => !this.contains(record.target))) this.scheduleSync();
      });
      this.observer.observe(this.section, { subtree: true, childList: true, characterData: true, attributes: true,
        attributeFilter: ['data-selected', 'aria-selected', 'disabled', 'aria-busy', 'data-variant-id', 'hidden'] });
      window.addEventListener('scroll', () => this.updateVisibility(), { signal, passive: true });
      window.addEventListener('resize', () => this.scheduleSync(), { signal, passive: true });
      this.sync();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.spacer?.remove();
      cancelAnimationFrame(this.frame);
    }

    scheduleSync() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.sync());
    }

    activeOption() {
      return this.source?.querySelector('[data-option][data-selected]');
    }

    groups() {
      if (!this.source) return [];
      const scope = this.source.matches('maison-product-duo') ? this.activeOption() : this.source;
      if (!scope) return [];
      const groups = [...scope.querySelectorAll('[data-mount-picker], [data-opt-field]')]
        .filter((group) => group.querySelector('[data-mount-trigger], [data-trigger]')?.getClientRects().length);
      if (!groups.length && this.source.matches('maison-product-buy') && this.source.querySelector('[data-trigger]')) return [this.source];
      return groups;
    }

    groupKey(index) {
      const slot = this.source.matches('maison-product-duo') ? this.activeOption()?.dataset.slot || '' : '';
      return `${this.source.closest('[data-block-id]')?.dataset.blockId}:${slot}:${index}`;
    }

    ready() {
      return Boolean(this.source) && this.groups().every((_, index) => this.confirmed.has(this.groupKey(index)));
    }

    setText(selector, value) {
      const node = this.querySelector(selector);
      if (node && node.textContent !== value) node.textContent = value;
    }

    sync() {
      this.source = [...this.section.querySelectorAll('maison-product-bundle, maison-product-duo, maison-product-buy')]
        .find((source) => source.getClientRects().length);
      this.sourceButton = this.source?.querySelector('[data-add-to-cart]');
      if (!this.sourceButton) { this.hidden = true; return; }

      const option = this.activeOption();
      const optionTitle = option?.querySelector('.maison-pb__option-title, .maison-duo__title')?.textContent.trim();
      this.setText('[data-sticky-title]', optionTitle ? `${this.defaultTitle} · ${optionTitle}` : this.defaultTitle);
      const image = option?.dataset.bundleImage || this.source.querySelector('[data-mount-image]')?.src || this.defaultImage;
      if (this.image && image && this.image.getAttribute('src') !== image) {
        this.image.removeAttribute('srcset');
        this.image.src = image;
      }
      const groups = this.groups();
      const ready = this.ready();
      this.camera.hidden = !groups.length;
      const cameraChoice = !this.source.matches('maison-product-buy') || groups.some((group) => /mount|camera/i.test(group.querySelector('.maison-buy__label')?.textContent || ''));
      this.camera.textContent = cameraChoice ? (ready ? 'Change camera mount' : 'Choose camera') : (ready ? 'Change options' : 'Choose options');
      this.mount.hidden = !ready || !groups.length;
      this.setText('[data-sticky-mount]', ready ? groups.map((group) => group.querySelector('[data-mount-value], [data-value]')?.textContent.trim()).filter(Boolean).join(' / ') : '');

      // Read the offer's displayed total; the product's base price may exclude bundle extras.
      let price, compare;
      if (option) {
        price = option.querySelector('.maison-pb__option-amount, [data-total]');
        compare = option.querySelector('.maison-pb__option-compare, [data-total-compare]');
      } else if (this.source.matches('maison-product-buy')) {
        const selected = this.source.selected?.();
        const data = selected?.dataset;
        const quantity = this.source.quantity?.() || 1;
        if (data && this.source.formatMoney) {
          price = { textContent: this.source.formatMoney(Number(data.variantPrice) * quantity) };
          compare = { textContent: this.source.formatMoney(Number(data.variantCompare) * quantity), hidden: Number(data.variantCompare) <= Number(data.variantPrice) };
        }
      }
      price ||= this.section.querySelector('.maison-price__current');
      compare ||= this.section.querySelector('.maison-price__compare');
      if (price) this.setText('[data-sticky-price]', price.textContent.trim());
      this.setText('[data-sticky-compare]', compare?.textContent.trim() || '');
      this.querySelector('[data-sticky-compare]').hidden = !compare || compare.hidden;

      // Use the same scoped button variables and markup, without another purchase handler.
      this.action.dataset.blockId = this.source.closest('[data-block-id]')?.dataset.blockId || '';
      if (!this.button) {
        this.button = document.createElement('button');
        this.button.type = 'button';
        this.action.append(this.button);
      }
      this.button.className = this.sourceButton.className;
      if (this.button.innerHTML !== this.sourceButton.innerHTML) {
        this.button.innerHTML = this.sourceButton.innerHTML;
        this.button.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
      }
      const stickyLabel = this.sourceButton.dataset.stickyLabel;
      this.button.classList.toggle('maison-sticky-cart__button--compact', Boolean(stickyLabel));
      const stickyText = this.button.querySelector('[data-cta-text]');
      if (stickyLabel && stickyText) {
        stickyText.textContent = stickyLabel;
        this.button.setAttribute('aria-label', this.sourceButton.querySelector('[data-cta-text]')?.textContent.trim() || stickyLabel);
      } else {
        this.button.removeAttribute('aria-label');
      }
      this.button.disabled = Boolean(this.source.busy || (ready && this.sourceButton.disabled));
      this.button.setAttribute('aria-busy', String(Boolean(this.source.busy)));
      this.updateVisibility();
    }

    updateVisibility() {
      // Only show after the original purchase controls have scrolled above the viewport.
      const top = this.stickyOffset();
      this.hidden = !this.sourceButton?.isConnected || this.sourceButton.getBoundingClientRect().bottom > top;
    }

    stickyOffset() {
      // Impact's --sticky-area-height is a calc() expression, not a parseable pixel value.
      const styles = getComputedStyle(document.documentElement);
      const value = (name) => parseFloat(styles.getPropertyValue(name)) || 0;
      const header = document.querySelector('store-header[sticky]');
      const headerHeight = header ? header.getBoundingClientRect().height : value('--sticky-header-enabled') * value('--header-height');
      return headerHeight +
        value('--sticky-announcement-bar-enabled') * value('--announcement-bar-height');
    }

    scrollToSelection() {
      const groups = this.groups();
      const group = groups.find((_, index) => !this.confirmed.has(this.groupKey(index))) || groups[0] || this.source;
      if (!group) return;
      const target = group.querySelector('[data-mount-trigger], [data-trigger]') || this.sourceButton;
      const top = this.stickyOffset();
      target?.focus({ preventScroll: true });
      window.scrollTo({ top: window.scrollY + group.getBoundingClientRect().top - top - 24,
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }
  });
})();
